import { Message, Course, SearchData, WebhookCard } from '@/types/chat';
import { trackSearch, getCurrentUserId, getOrCreateSessionId } from '@/utils/courseAnalyticsService';

export interface ChatApiResponse {
  message?: string;
  searchReady?: boolean;
  searchData?: SearchData;
  mentorQuery?: boolean;
  mentorData?: {
    mentor_name: string;
  };
}

type SearchWebhookResponse =
  | {
      matchCount?: number;
      userSummary?: string;
      courses?: any[];
      content?: string;
    }
  | any[];

async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  maxRetries: number = 3,
  timeout?: number
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      if (timeout) {
        timeoutId = setTimeout(() => controller.abort(), timeout);
      }

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      if (timeoutId) clearTimeout(timeoutId);

      if (response.status === 429 && attempt < maxRetries) {
        console.log(`[Retry ${attempt}/${maxRetries}] Rate limit hit (429), retrying...`);
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      if (!response.ok && response.status >= 500 && attempt < maxRetries) {
        console.log(`[Retry ${attempt}/${maxRetries}] Server error (${response.status}), retrying...`);
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      return response;
    } catch (error: any) {
      lastError = error;

      if (error.name === 'AbortError') {
        console.error(`[Retry ${attempt}/${maxRetries}] Request timeout`);
      } else {
        console.error(`[Retry ${attempt}/${maxRetries}] Network error:`, error.message);
      }

      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Max retries exceeded');
}

function asText(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function normalizeWebhookEnvelope(raw: SearchWebhookResponse): {
  matchCount: number;
  userSummary: string;
  courses: any[];
} {
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (first && typeof first === 'object') {
      const courses = Array.isArray((first as any).courses) ? (first as any).courses : [];
      const userSummary =
        asText((first as any).userSummary) ||
        asText((first as any).content);

      return {
        matchCount: courses.length,
        userSummary,
        courses,
      };
    }

    return {
      matchCount: 0,
      userSummary: '',
      courses: [],
    };
  }

  const obj = raw && typeof raw === 'object' ? raw : {};
  const courses = Array.isArray((obj as any).courses) ? (obj as any).courses : [];

  return {
    matchCount:
      typeof (obj as any).matchCount === 'number'
        ? (obj as any).matchCount
        : courses.length,
    userSummary:
      asText((obj as any).userSummary) ||
      asText((obj as any).content),
    courses,
  };
}

function mapWebhookCardToCourse(card: any): Course | null {
  if (!card || typeof card !== 'object') return null;

  const id =
    asText(card.id) ||
    asText(card.course_id);

  if (!id) return null;

  const marketingTitle = asText(card.marketing_title);
  const title = asText(card.title);
  const provider = asText(card.provider);

  const course: Course = {
    ...card,
    id,
    course_id: asText(card.course_id) || id,
    name: marketingTitle || title || `קורס ${id}`,
    title: title || undefined,
    description: asText(card.summary) || asText(card.description) || '',
    mentor: provider || 'ספק לא צוין',
    provider: provider || undefined,
    price: asText(card.price) || 'לא צוין',
    learningType: asText(card.format) || asText(card.learningType) || 'לא צוין',
    format: asText(card.format) || undefined,
    duration: asText(card.duration) || 'לא צוין',
    imageUrl: asText(card.imageUrl) || undefined,
    images: asText(card.images) || undefined,
    audience: asText(card.audience) || undefined,
    summary: asText(card.summary) || undefined,
    landingUrl: asText(card.landingUrl) || undefined,
    deliverables: asText(card.deliverables) || undefined,
    outcomes: asText(card.outcomes) || undefined,
    uniqueness: asText(card.uniqueness) || undefined,
    target_skills: asText(card.target_skills) || undefined,
    marketing_title: marketingTitle || undefined,
    language: asText(card.language) || undefined,
    level: asText(card.level) || undefined,
    focus: asText(card.focus) || undefined,
    topic: asText(card.topic) || undefined,
    type: asText(card.type) || undefined,
    source_urls: asText(card.source_urls) || undefined,
    sourceUrl: asText(card.sourceUrl) || undefined,
    contact: asText(card.contact) || undefined,
    matchReason: asText(card.matchReason) || asText(card.match_reason) || undefined,
    match_reason: asText(card.match_reason) || asText(card.matchReason) || undefined,
    relevant: asText(card.relevant) || undefined,
    source_url_key: asText(card.source_url_key) || undefined,
  };

  return course;
}

export class ChatService {
  private supabaseUrl: string;
  private supabaseKey: string;

  constructor() {
    this.supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL || '';
    this.supabaseKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '';

    if (!this.supabaseUrl || !this.supabaseKey) {
      console.warn('Supabase credentials not configured');
    }
  }

  async sendChatMessage(
    messages: Message[],
    displayedCourses?: Course[]
  ): Promise<ChatApiResponse> {
    const apiUrl = `${this.supabaseUrl}/functions/v1/chat`;

    const response = await fetchWithRetry(
      apiUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.supabaseKey}`,
        },
        body: JSON.stringify({
          messages,
          displayedCourses:
            displayedCourses && displayedCourses.length > 0
              ? displayedCourses
              : undefined,
        }),
      },
      3,
      30000
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Chat API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  }

  async sendToWebhook(
    searchData: SearchData
  ): Promise<{ matchCount: number; userSummary: string; courses?: Course[] }> {
    const apiUrl = `${this.supabaseUrl}/functions/v1/search-courses`;

    const summary = searchData.summary || '';
    if (!summary || summary.trim().length === 0) {
      throw new Error('Summary is required for course search');
    }

    if (summary.length > 5000) {
      throw new Error('Summary is too long (max 5000 characters)');
    }

    if (!searchData.topic || searchData.topic.trim().length === 0) {
      throw new Error('Topic is required for course search');
    }

    const response = await fetchWithRetry(
      apiUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.supabaseKey}`,
          apikey: this.supabaseKey,
        },
        body: JSON.stringify({ searchData }),
      },
      3
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Search failed with status ${response.status}`);
    }

    const rawResult: SearchWebhookResponse = await response.json();
    const normalized = normalizeWebhookEnvelope(rawResult);

    const mappedCourses = normalized.courses
      .map((card) => mapWebhookCardToCourse(card))
      .filter(Boolean) as Course[];

    const result = {
      matchCount: mappedCourses.length > 0 ? mappedCourses.length : normalized.matchCount || 0,
      userSummary: normalized.userSummary || summary,
      courses: mappedCourses,
    };

    (async () => {
      try {
        const userId = await getCurrentUserId();
        const sessionId = getOrCreateSessionId();

        await trackSearch({
          conversationId: null,
          userId,
          sessionId,
          topic: searchData.topic,
          subTopic: searchData.subtopic,
          learningType:
            (searchData as any).learning_type ||
            (searchData as any).learning_format,
          budget: searchData.budget,
          location: searchData.location,
          coursesFound: result.courses?.length || 0,
          searchDurationMs: null,
        });
      } catch (error) {
        console.error('Failed to track search analytics:', error);
      }
    })();

    return result;
  }

  async generateCourseInsight(
    type: 'analysis' | 'bottomLine' | 'differences',
    courses: Course[],
    searchData: SearchData | null
  ): Promise<string> {
    const apiUrl = `${this.supabaseUrl}/functions/v1/chat`;

    const coursesDetails = courses
      .map((course, idx) => {
        let details = `קורס ${idx + 1}: ${course.name}\n`;
        details += `ספק: ${course.mentor}\n`;
        if (course.description) details += `תיאור: ${course.description}\n`;
        if (course.learningType) details += `סוג: ${course.learningType}\n`;
        if (course.price) details += `מחיר: ${course.price}\n`;
        if (course.duration) details += `משך: ${course.duration}\n`;
        if (course.audience) details += `קהל יעד: ${course.audience}\n`;
        if (type !== 'bottomLine') {
          if (course.deliverables) details += `מה תקבל: ${course.deliverables}\n`;
          if (course.outcomes) details += `תוצאות: ${course.outcomes}\n`;
          if (course.uniqueness) details += `ייחודיות: ${course.uniqueness}\n`;
          if (course.target_skills) details += `כישורים: ${course.target_skills}\n`;
        }
        return details;
      })
      .join('\n---\n\n');

    const prompts = {
      analysis: `נתח והשווה את הקורסים האלה. כלול: יתרונות/חסרונות, למי מתאים, המלצה, נקודות חשובות.\n\n${coursesDetails}`,
      bottomLine: `המלץ על הקורס המתאים ביותר מהרשימה.
החזר את התשובה בפורמט JSON בדיוק כך:
{"courseName": "שם הקורס המדויק כפי שמופיע ברשימה", "recommendation": "משפט קצר של 1-2 שורות למה הוא הכי מתאים"}

דוגמה:
{"courseName": "קורס פיתוח Full Stack", "recommendation": "הקורס הזה מושלם בשבילך כי הוא משלב תאוריה ופרקטיקה, ומתאים במיוחד למי שרוצה לעבור קריירה לתכנות"}

רשימת הקורסים:
${coursesDetails}`,
      differences: `סכם ההבדלים העיקריים: משך, פורמט, תוצאות. תמציתי וברור.\n\n${coursesDetails}`,
    };

    const response = await fetchWithRetry(
      apiUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.supabaseKey}`,
        },
        body: JSON.stringify({
          messages: [
            {
              id: `${type}-req`,
              role: 'user',
              content: prompts[type],
              timestamp: Date.now(),
            },
          ],
        }),
      },
      3,
      30000
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[generateCourseInsight] API error:', response.status, errorText);
      throw new Error(`Failed to generate ${type}: ${response.status}`);
    }

    const data = await response.json();

    if (!data.message?.trim()) {
      console.error('[generateCourseInsight] Empty response');
      throw new Error(`Empty ${type} response`);
    }

    return data.message;
  }

  async generateCourseAnalysis(courses: Course[], searchData: SearchData | null): Promise<string> {
    return this.generateCourseInsight('analysis', courses, searchData);
  }

  async generateBottomLine(
    courses: Course[],
    searchData: SearchData | null
  ): Promise<{ courseName: string; recommendation: string }> {
    const responseText = await this.generateCourseInsight('bottomLine', courses, searchData);

    try {
      const jsonMatch = responseText.match(/\{[\s\S]*"courseName"[\s\S]*"recommendation"[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.courseName && parsed.recommendation) {
          return {
            courseName: parsed.courseName.trim(),
            recommendation: parsed.recommendation.trim(),
          };
        }
      }
    } catch (e) {
      console.warn('[generateBottomLine] Failed to parse JSON, falling back to first course');
    }

    return {
      courseName: courses[0]?.name || '',
      recommendation: responseText,
    };
  }

  async generateDifferencesSummary(courses: Course[], searchData: SearchData | null): Promise<string> {
    return this.generateCourseInsight('differences', courses, searchData);
  }
}

export const chatService = new ChatService();